/* eslint-disable @typescript-eslint/no-explicit-any */
let youtubeApiPromise: Promise<any> | null = null

export type YouTubePlayer = {
	playVideo: () => void
	pauseVideo: () => void
	cueVideoById: (videoId: string, startSeconds?: number) => void
	seekTo: (seconds: number, allowSeekAhead?: boolean) => void
	getCurrentTime: () => number
	getDuration: () => number
	getPlayerState: () => number
	mute: () => void
	isMuted: () => boolean
	getIframe: () => HTMLIFrameElement
	destroy: () => void
}

export type YouTubePlayerEvent = {
	data: number
}

export type YouTubeIframeApi = {
	Player: new (
		container: HTMLDivElement,
		config: {
			videoId: string
			playerVars: Record<string, string | number>
			events: {
				onReady?: () => void
				onStateChange?: (event: YouTubePlayerEvent) => void
			}
		}
	) => YouTubePlayer
	PlayerState: {
		ENDED: number
		PLAYING: number
		PAUSED: number
		BUFFERING: number
	}
}

export const loadYouTubeIframeApi = (): Promise<any> => {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('Window is not available'))
	}

	if (window.YT && window.YT.Player) {
		return Promise.resolve(window.YT)
	}

	if (!youtubeApiPromise) {
		youtubeApiPromise = new Promise((resolve, reject) => {
			const existingScript = document.getElementById('youtube-iframe-api')
			if (!existingScript) {
				const script = document.createElement('script')
				script.id = 'youtube-iframe-api'
				script.src = 'https://www.youtube.com/iframe_api'
				script.async = true
				script.onerror = () => reject(new Error('Failed to load YouTube API'))
				document.body.appendChild(script)
			}

			const previousReady = window.onYouTubeIframeAPIReady
			window.onYouTubeIframeAPIReady = () => {
				if (typeof previousReady === 'function') {
					previousReady()
				}
				resolve(window.YT)
			}
		})
	}

	return youtubeApiPromise
}

export const extractYouTubeVideoId = (link: string): string | null => {
	try {
		const url = new URL(link)
		const host = url.hostname.toLowerCase()

		if (host === 'youtu.be' || host.endsWith('.youtu.be')) {
			const id = url.pathname.replace('/', '').trim()
			return id || null
		}

		if (host.includes('youtube.com')) {
			if (url.pathname === '/watch') {
				return url.searchParams.get('v')
			}

			if (url.pathname.startsWith('/embed/')) {
				return url.pathname.replace('/embed/', '').split('/')[0] || null
			}

			if (url.pathname.startsWith('/v/')) {
				return url.pathname.replace('/v/', '').split('/')[0] || null
			}

			if (url.pathname.startsWith('/shorts/')) {
				return url.pathname.replace('/shorts/', '').split('/')[0] || null
			}
		}
	} catch {
		return null
	}

	return null
}

declare global {
	interface Window {
		YT?: any
		onYouTubeIframeAPIReady?: () => void
	}
}
